import React, { useCallback, useMemo, useState, useRef } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { RadioButtonGroup } from '@techstyle/react-components';
import { visuallyHidden } from '@techstyle/react-components-legacy/styles';
import { FormattedMessage, useIntl } from '@techstyle/react-intl';

import { desktop } from '../../styles';
import { Component as StaticIcon } from '../StaticIcon';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const ColorRadioGroup = styled(RadioButtonGroup)`
  display: flex;
  flex-wrap: wrap;

  ${desktop`
    order: 1;
    justify-content: flex-start;
  `};
`;

const MoreButton = styled.button`
  padding: ${props => props.theme.spacing.tiny}px;

  background-color: ${props => props.theme.colors.gray150};
  border-radius: 14px;
  font-size: 10px;
  cursor: pointer;

  ${desktop`
    align-self: flex-start;
    order: 2;
  `};
`;

/**
 * Use real radio button inputs for browser UX and accessibility. We could use
 * a bunch of ARIA features to replicate this, but then we'd still be missing
 * the keyboard handling that the browser provides and would have to code it up
 * ourselves.
 */
const HiddenInput = styled.input.attrs({
  type: 'radio',
})`
  /* Hide the real radio button so we can render our own. */
  ${visuallyHidden};
`;

export const Color = styled.span`
  display: inline-block;
  height: 26px;
  width: 26px;
  vertical-align: top;

  border-radius: 100%;
  background-color: ${props =>
    /^rgb/.test(props.value) || /^#/.test(props.value)
      ? props.value
      : props.theme.colors.gray150};
  background-image: ${props =>
    /^rgb/.test(props.value) || /^#/.test(props.value)
      ? 'none'
      : `url(${props.value})`};
  background-clip: content-box;
  background-size: cover;
  cursor: pointer;

  input:checked + & {
    padding: 2px;

    border: 2px solid ${props => props.theme.colors.gunmetal};
  }

  input:focus + & {
    /* Since the real input is hidden, it won't render an outline when focused.
       We need some focus state for accessibility. */
    box-shadow: 0 0 1px 3px ${props => props.theme.colors.focusShadow};
  }

  ${props =>
    props.showUnavailable &&
    `
      position: relative;
      overflow: hidden;

      &:before,
      &:after {
        content: " ";
        position: absolute;
        left: 0;

        background-color: ${props.theme.colors.white};
        width: 2px;
        height: 150%; /* The diagonal of a square is longer than its sides. */
      }

      &:before {
        bottom: 1px;
        transform-origin: bottom left;
        transform: rotate(45deg);
      }

      &:after {
        top: 1px;
        transform: rotate(-45deg);
      }
    `};
`;

const ColorRadioButton = styled.label`
  margin-bottom: ${props => props.theme.spacing.tiny}px;
  margin-left: ${props => props.theme.spacing.small}px;
  position: relative;
`;

const RadioButtonLabel = styled.span`
  /* Each color label should be rendered, but visually hidden, for
     accessibility. Screen readers will still read each color name. */
  ${visuallyHidden};
`;

const NoColorLabel = styled.label`
  font-size: 12px;
  font-weight: 200;
`;

const ColorLabelWrapper = styled.div`
  ${({ theme }) => theme.paragraph.variants.paragraph4Uppercase.textStyles}
`;

const ColorName = styled.span`
  text-align: left;
  margin-bottom: ${props => props.theme.spacing.tiny}px;

  ${desktop`
    margin-bottom: ${props => props.theme.spacing.small}px;
  `};

  display: flex;
  flex-direction: row;
  gap: 20px;
  align-items: center;
`;

const ColorTitle = styled.div`
  ${({ theme }) => theme.paragraph.variants.paragraph4Uppercase.textStyles}
  color: ${({ theme }) => theme.colors.grey};
`;

const UnlockIconWrapper = styled.div`
  display: flex;
  position: absolute;
  right: -4px;
  bottom: -1px;
  color: ${props => props.theme.colors.white};
  background: ${props => props.theme.colors.default};
  border-radius: 50%;
  padding: 3px;
  border: 1px solid ${props => props.theme.colors.white};
`;

const UnlockIcon = styled(StaticIcon).attrs({
  type: 'unlock',
  size: 9,
})``;

let inputNameCounter = 0;

export default function ColorSwatch({
  className,
  colors,
  inputName,
  limit,
  onChange,
  showName,
  value,
  isColorQuantityAvailable,
  isPreorderable,
  shouldOnlyShowAvailableColors,
  shouldHideColorSwatches,
  isGiftCards = false,
}) {
  const { formatMessage } = useIntl();

  // Browsers needs radio inputs to have a name, even if we don't have a
  // `<form>` or won't submit the `<form>` via traditional means. If they
  // don't have a name, it considers every such radio input on the page to be
  // in the same group, which messes up keyboard interaction, selected states,
  // etc. (Consider rendering multiple color swatches on the page, like in the
  // styleguide.) This uses a typicalsolution for generating input IDs and
  // names in React: use a global counter and generate them. They'll only be
  // used if the user doesn't pass their own `inputName` prop.
  const finalInputName = useRef(inputName);
  if (!finalInputName.current) {
    finalInputName.current = `color-${++inputNameCounter}`;
  }

  function showMoreButton() {
    let showMore = false;
    if (limit) {
      const isColorVisible = colorOptions.indexOf(selectedColor) < limit;
      // Check that limit is greater than number of color options
      const isLongList = colorOptions.length > limit;

      showMore = isColorVisible && isLongList;
    }
    return showMore;
  }

  /**
   * Instead of just passing the event to `onChange`, also pass the matching
   * color object.
   */
  const handleChange = useCallback(
    event => {
      if (onChange) {
        const color = colors.find(
          color =>
            `${color.value == null ? '' : color.value}` === event.target.value
        );
        onChange(event, color || null);
      }
    },
    [colors, onChange]
  );

  const [userDidExpand, setExpanded] = useState(false);

  const colorOptions = useMemo(
    () =>
      colors.map(color => {
        return {
          ...color,
          label: color.name,
          colorUrl: color.colorUrl,
          value: color.value,
        };
      }),
    [colors]
  );

  const selectedColor = useMemo(() => {
    return colorOptions.find(color => color.value === value);
  }, [colorOptions, value]);

  const isLimited = showMoreButton() && !userDidExpand;

  const renderColorOptions = isLimited
    ? colorOptions.slice(0, limit)
    : colorOptions;

  const nbsp = '\u00a0';

  function renderColorRadioButton(colorOption) {
    const {
      availableQuantity,
      availablePreorderQuantity,
      key,
      disabled,
      label,
      inputName,
      isUnlockedXclusive,
      value,
      colorUrl,
    } = colorOption;
    const quantityToCheck = isPreorderable
      ? availablePreorderQuantity
      : availableQuantity;
    const checked =
      selectedColor?.value == null ? undefined : value === selectedColor.value;

    return (
      <ColorRadioButton
        key={key || value}
        disabled={disabled}
        title={label}
        data-autotag={`${
          quantityToCheck === 0
            ? `pdp-color-swatch-sold-out pdp-color-swatch`
            : 'pdp-color-swatch'
        }`}
      >
        <HiddenInput
          data-testid={`color-${label}`}
          name={inputName}
          value={value}
          disabled={disabled}
          checked={checked}
          onChange={handleChange}
        />
        <Color
          value={colorUrl}
          disabled={disabled}
          showUnavailable={quantityToCheck <= 0}
        />
        <RadioButtonLabel>{label}</RadioButtonLabel>
        {isUnlockedXclusive ? (
          <UnlockIconWrapper
            aria-label={formatMessage({
              id: 'global_checkout.unlocked_xclusive_label',
              defaultMessage: 'Unlocked Xclusive',
            })}
          >
            <UnlockIcon />
          </UnlockIconWrapper>
        ) : null}
      </ColorRadioButton>
    );
  }

  function renderNoColorAvailableLabel() {
    if (selectedColor && !isColorQuantityAvailable && selectedColor.isBra) {
      return (
        <NoColorLabel>
          <FormattedMessage
            defaultMessage="No colors available"
            id="site_pdp.no_colors_available"
          />
        </NoColorLabel>
      );
    }
    return null;
  }

  const handleExpand = useCallback(() => setExpanded(true), []);

  return (
    <Container className={className}>
      {isGiftCards ? (
        <ColorName>
          <ColorTitle>
            <FormattedMessage
              defaultMessage="Choose A Design"
              id="giftcard.giftcard_color_swatch_label"
            />
          </ColorTitle>
          {selectedColor ? (
            <ColorLabelWrapper>{selectedColor.label}</ColorLabelWrapper>
          ) : (
            nbsp
          )}
        </ColorName>
      ) : (
        nbsp
      )}
      {showName && !isGiftCards ? (
        <ColorName>
          <ColorTitle>
            <FormattedMessage
              defaultMessage="Color"
              id="site_pdp.color_swatch_label"
            />
          </ColorTitle>
          {selectedColor ? (
            <ColorLabelWrapper>{selectedColor.label}</ColorLabelWrapper>
          ) : (
            nbsp
          )}
        </ColorName>
      ) : null}
      {shouldHideColorSwatches ? null : (
        <ColorRadioGroup name={finalInputName.current}>
          {renderColorOptions.map(colorOption =>
            renderColorRadioButton(colorOption)
          )}
        </ColorRadioGroup>
      )}
      {isLimited ? (
        <MoreButton onClick={handleExpand}>
          <FormattedMessage
            defaultMessage="+ More Colors"
            id="site_pdp.color_swatch_label_colors"
          />
        </MoreButton>
      ) : null}
      {renderNoColorAvailableLabel()}
    </Container>
  );
}

ColorSwatch.Color = Color;
ColorSwatch.ColorName = ColorName;
ColorSwatch.ColorRadioButton = ColorRadioButton;
ColorSwatch.ColorRadioGroup = ColorRadioGroup;
ColorSwatch.ColorTitle = ColorTitle;

ColorSwatch.propTypes = {
  /**
   * Class name(s) to apply to the container element.
   */
  className: PropTypes.string,
  /**
   * Colors to render in the color swatch.
   */
  colors: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      }),
    ])
  ).isRequired,
  /**
   * The `name` to use on the hidden radio input elements. If you don't supply
   * one, a name prefixed with `color-` will be generated for you.
   */
  inputName: PropTypes.string,
  isColorQuantityAvailable: PropTypes.bool,
  isPreorderable: PropTypes.bool,
  limit: PropTypes.number,
  onChange: PropTypes.func,
  shouldOnlyShowAvailableColors: PropTypes.bool,
  /**
   * Whether to show the currently selected color name.
   */
  showName: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isGiftCards: PropTypes.bool,
};

ColorSwatch.defaultProps = {
  isColorQuantityAvailable: true,
  showName: false,
};
